/**
 * The single blocking-risk verdict for one job detail page.
 *
 * The job detail page already renders full-fidelity risk notices — the scam
 * caution banner, the geo-fit badge, the broken/unverified apply-link notes.
 * Since PR #3293 the blocking ones sit in a strip directly under the `<h1>`,
 * which fixed *the strip*. It did not fix the button: on mobile the tappable
 * Apply surface is a `fixed bottom-0` bar that is on screen from first paint
 * and stays there while the user scrolls past every notice. A seeker who taps
 * it after scrolling has the warning nowhere in view.
 *
 * So the same state is reduced to ONE short line and mirrored onto the footer
 * bar itself. This module owns that reduction so the strip and the mirror can
 * never disagree about which risk is the one worth saying — precedence lives
 * here, not in JSX.
 */

/**
 * Which risk the mirror is reporting. Ordered by precedence: the first one
 * that applies is the one shown, because a 65px bar has room for one line and
 * the seeker needs the reason that most changes their decision.
 *
 * - `scam` — deterministic employment-fraud tells (upfront payment, etc.).
 *   Nothing outranks "this posting may be trying to take your money".
 * - `geo_restricted` — the employer hires only in regions the seeker is not
 *   in. Applying cannot succeed, however good the role is.
 * - `apply_broken` — applicants reported the apply form does not open.
 * - `apply_unverified` — we could not resolve the apply destination. A caveat,
 *   not a blocker; it is last because promoting it would train the strip to be
 *   ignored.
 */
export type ApplyRiskKind =
	| "scam"
	| "geo_restricted"
	| "apply_broken"
	| "apply_unverified";

export type ApplyRiskInput = {
	/** Job carries at least one deterministic scam signal. */
	hasScamSignal: boolean;
	/** Stored `jobs.geo_fit` resolved to `"restricted"`. */
	geoRestricted: boolean;
	/** `jobs.apply_health_flag === "reported_broken"`. */
	applyBroken: boolean;
	/** Derived apply health is `"unresolved"` (no reachable apply route). */
	applyUnverified: boolean;
};

/** Reduce the risk set to the single kind worth putting on the Apply bar. */
export function pickApplyRisk(input: ApplyRiskInput): ApplyRiskKind | null {
	if (input.hasScamSignal) return "scam";
	if (input.geoRestricted) return "geo_restricted";
	if (input.applyBroken) return "apply_broken";
	if (input.applyUnverified) return "apply_unverified";
	return null;
}

const MESSAGES: Record<ApplyRiskKind, { id: string; en: string }> = {
	scam: {
		id: "Ada tanda penipuan pada lowongan ini — periksa dulu sebelum melamar.",
		en: "This posting carries fraud warning signs — check it before applying.",
	},
	geo_restricted: {
		id: "Perusahaan ini tampaknya hanya merekrut di wilayah tertentu.",
		en: "This employer appears to hire only in certain regions.",
	},
	apply_broken: {
		id: "Pelamar lain melaporkan form lamaran ini sulit dibuka.",
		en: "Other applicants reported this application form fails to open.",
	},
	apply_unverified: {
		id: "Tautan lamaran ini belum terverifikasi.",
		en: "This apply link is not verified yet.",
	},
};

/**
 * One-line copy for the footer mirror. Deliberately shorter than the full
 * notice it mirrors — the mirror's job is "do not tap blind", and the full
 * reasoning is already on the page above.
 */
export function applyRiskMessage(kind: ApplyRiskKind, locale: string): string {
	return locale === "id" ? MESSAGES[kind].id : MESSAGES[kind].en;
}
